/* Armodia — Birdy Digital */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Reveal on scroll ---------- */
  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-visible');
              revealObserver.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -8% 0px' }
        )
      : null;
  const initReveal = (root = document) => {
    root.querySelectorAll('.arm-reveal:not(.is-visible)').forEach((el) => {
      if (!revealObserver || reduceMotion) el.classList.add('is-visible');
      else revealObserver.observe(el);
    });
  };

  /* ---------- Şeffaf header ---------- */
  const initTransparentHeader = () => {
    const first = document.querySelector('main > .shopify-section:first-child [data-arm-transparent-header]');
    document.body.classList.toggle('arm-header-transparent', !!first);
    const section = document.querySelector('.section-header');
    if (!first || !section) return;
    const update = () => {
      const solid = window.scrollY > 40 || section.matches(':hover') || section.querySelector('details[open]');
      section.classList.toggle('arm-header-solid', !!solid);
    };
    if (!section.dataset.armBound) {
      section.dataset.armBound = '1';
      window.addEventListener('scroll', update, { passive: true });
      section.addEventListener('mouseenter', update);
      section.addEventListener('mouseleave', () => setTimeout(update, 50));
      section.addEventListener('toggle', update, true);
    }
    update();
  };

  /* ---------- Slideshow ---------- */
  class ArmSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.arm-slide')];
      this.dots = [...this.querySelectorAll('.arm-slideshow__dot')];
      this.index = 0;
      this.speed = parseInt(this.dataset.speed || '5', 10) * 1000;
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i, true)));
      this.querySelector('.arm-slideshow__next-section')?.addEventListener('click', () => {
        const next = this.closest('.shopify-section')?.nextElementSibling;
        next && window.scrollTo({ top: next.getBoundingClientRect().top + window.scrollY - 0, behavior: 'smooth' });
      });
      this.querySelector('[data-arm-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
      this.querySelector('[data-arm-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
      this.bindSwipe();
      this.go(0);
      if (this.dataset.autoplay === 'true' && this.slides.length > 1 && !reduceMotion) this.play();
      this.addEventListener('shopify:block:select', (e) => {
        this.stop();
        this.go(this.slides.indexOf(e.target));
      });
    }
    disconnectedCallback() {
      this.stop();
    }
    bindSwipe() {
      let x = null;
      this.addEventListener('touchstart', (e) => (x = e.touches[0].clientX), { passive: true });
      this.addEventListener('touchend', (e) => {
        if (x === null) return;
        const dx = e.changedTouches[0].clientX - x;
        if (Math.abs(dx) > 50) this.go(this.index + (dx < 0 ? 1 : -1), true);
        x = null;
      });
    }
    go(i, user = false) {
      if (!this.slides.length) return;
      this.index = (i + this.slides.length) % this.slides.length;
      this.slides.forEach((s, n) => {
        const active = n === this.index;
        s.classList.toggle('is-active', active);
        s.setAttribute('aria-hidden', active ? 'false' : 'true');
        s.querySelectorAll('a, button').forEach((el) => (el.tabIndex = active ? 0 : -1));
        s.querySelectorAll('video').forEach((v) => (active ? v.play?.().catch(() => {}) : v.pause?.()));
      });
      this.dots.forEach((d, n) => {
        d.classList.toggle('is-active', n === this.index);
        d.setAttribute('aria-current', n === this.index ? 'true' : 'false');
      });
      this.style.setProperty('--arm-progress-duration', `${this.speed}ms`);
      if (user && this.timer) this.play();
    }
    play() {
      this.stop();
      this.timer = setInterval(() => this.go(this.index + 1), this.speed);
    }
    stop() {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  /* ---------- Önce / Sonra ---------- */
  class ArmBeforeAfter extends HTMLElement {
    connectedCallback() {
      this.range = this.querySelector('input[type="range"]');
      const set = () => this.style.setProperty('--arm-split', `${this.range.value}%`);
      this.range?.addEventListener('input', set);
      set();
    }
  }

  /* ---------- Sekmeler ---------- */
  class ArmTabs extends HTMLElement {
    connectedCallback() {
      this.buttons = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.buttons.forEach((btn, i) => {
        btn.addEventListener('click', () => this.select(i));
        btn.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') this.select((i + 1) % this.buttons.length, true);
          if (e.key === 'ArrowLeft') this.select((i - 1 + this.buttons.length) % this.buttons.length, true);
        });
      });
      this.addEventListener('shopify:block:select', (e) => {
        const i = this.panels.findIndex((p) => p.dataset.blockId === e.detail.blockId);
        if (i > -1) this.select(i);
      });
    }
    select(i, focus = false) {
      this.buttons.forEach((b, n) => {
        b.setAttribute('aria-selected', n === i ? 'true' : 'false');
        b.tabIndex = n === i ? 0 : -1;
      });
      this.panels.forEach((p, n) => {
        p.hidden = n !== i;
        if (n === i) initReveal(p);
      });
      if (focus) this.buttons[i].focus();
    }
  }

  /* ---------- Kombini Al (shop the look) ---------- */
  class ArmShopTheLook extends HTMLElement {
    connectedCallback() {
      this.looks = [...this.querySelectorAll('.arm-look')];
      this.index = 0;
      this.querySelector('[data-arm-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-arm-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.querySelectorAll('.arm-look__dot-nav').forEach((d, i) => d.addEventListener('click', () => this.go(i)));
      this.querySelectorAll('.arm-hotspot').forEach((spot) =>
        spot.addEventListener('click', () => this.focusProduct(spot.dataset.look, spot.dataset.product))
      );
      this.addEventListener('shopify:block:select', (e) => {
        const i = this.looks.findIndex((l) => l.dataset.blockId === e.detail.blockId);
        if (i > -1) this.go(i);
      });
      this.go(0);
    }
    go(i) {
      if (!this.looks.length) return;
      this.index = (i + this.looks.length) % this.looks.length;
      this.looks.forEach((l, n) => l.classList.toggle('is-active', n === this.index));
      this.querySelectorAll('.arm-look-products').forEach((p) => (p.hidden = p.dataset.look !== String(this.index)));
      this.querySelectorAll('.arm-look__dot-nav').forEach((d, n) => d.classList.toggle('is-active', n === this.index));
      const counter = this.querySelector('[data-arm-counter]');
      if (counter) counter.textContent = `${this.index + 1} / ${this.looks.length}`;
    }
    focusProduct(look, product) {
      const list = this.querySelector(`.arm-look-products[data-look="${look}"]`);
      if (!list) return;
      list.querySelectorAll('[data-product]').forEach((card) => card.classList.toggle('is-focused', card.dataset.product === product));
      const card = list.querySelector(`[data-product="${product}"]`);
      card?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }

  /* ---------- Yatay kaydırıcı okları ---------- */
  class ArmScroller extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-arm-track]');
      const step = () => this.track.clientWidth * 0.8;
      this.querySelector('[data-arm-prev]')?.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
      this.querySelector('[data-arm-next]')?.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: 'smooth' }));
    }
  }

  const define = (name, cls) => !customElements.get(name) && customElements.define(name, cls);
  define('arm-slideshow', ArmSlideshow);
  define('arm-before-after', ArmBeforeAfter);
  define('arm-tabs', ArmTabs);
  define('arm-shop-the-look', ArmShopTheLook);
  define('arm-scroller', ArmScroller);

  /* ---------- Seçili seçenek değeri etiketi ---------- */
  document.addEventListener('change', (e) => {
    const input = e.target;
    if (!input.matches('.arm-image-swatches input[type="radio"]')) return;
    const label = input.closest('fieldset')?.querySelector('.arm-selected-value');
    if (label) label.textContent = input.value;
  });

  const init = () => {
    initReveal();
    initTransparentHeader();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', (e) => {
    initReveal(e.target);
    initTransparentHeader();
  });
  document.addEventListener('shopify:section:unload', () => setTimeout(initTransparentHeader, 0));
  document.addEventListener('shopify:section:reorder', initTransparentHeader);
})();
